import {
    fetchProductFamilies,
    fetchShowsGst,
    postCheckoutState
} from "@/store/features/checkout/checkoutThunks";
import { CheckoutPagesEnum } from "@/ts/enums/CheckoutPagesEnum";
import { CHECKOUT_STUDENT_FLOW } from "@/ts/enums/CheckoutStudentFlowEnum";
import { REQUEST_STATUS } from "@/ts/enums/RequestStatus";
import { ProductFamilyInterface } from "@/ts/interfaces/Checkout/ProductFamilyInterface";
import { ProductFamilyApiInterface } from "@/ts/interfaces/Checkout/api/ProductFamilyApiInterface";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

const urlParams = new URLSearchParams(window.location.search);
const selectedState = urlParams.get("selectedState");
const selectedCountry = urlParams.get("selectedCountry");

interface UserSettingsState {
    productFamilies: ProductFamilyInterface[] | null;
    productFamiliesResponse: ProductFamilyApiInterface[] | null;
    userSelectedCountry: string;
    /**
     * Whether the "inc GST" suffix should render for this user. Hydrated by
     * CheckoutLayout via the `fetchShowsGst` thunk (one call per checkout
     * session), then overwritten if the user changes the billing address
     * country on the billing form. Defaults to `true` so guests, the
     * pre-fetch window, and failure cases all behave as AU customers
     * (BLD2-885).
     *
     * Backend origin: GET /api/parents/me/shows-gst. Kept off Inertia
     * shared props deliberately — the underlying query joins on unindexed
     * payment_details.cb_billing_customer_id, so scoping the fetch to
     * checkout avoids paying it on every authenticated Inertia request.
     *
     * This is a display-only concern (AD-6, BLD2-833). Charged amounts are
     * unaffected — Chargebee bills GST from the actual billing address.
     */
    showsGst: boolean;
    userSelectedState: string;
    userSelectedLang: string;
    currency: "AUD" | "USD";
    nextAttemptPage: CheckoutPagesEnum | null;
    parentUserUuid: string;
    isNewsletterSubEnabled: boolean;
    canShowExistingUserPage: boolean;
    checkoutToken: string | null;
    studentFlow: CHECKOUT_STUDENT_FLOW | null;
    isProvidedQueryParams: boolean;
    canAddonSelectionSkip: boolean;

    productFamiliesRequestStatus: REQUEST_STATUS;
    checkoutStateRequestStatus: REQUEST_STATUS;
    showsGstRequestStatus: REQUEST_STATUS;
}

const initialState: UserSettingsState = {
    productFamilies: null,
    productFamiliesResponse: null,
    userSelectedCountry: selectedCountry ?? "AU",
    showsGst: true,
    userSelectedState: selectedState ?? "",
    userSelectedLang: "en",
    currency: "AUD",
    nextAttemptPage: null,
    parentUserUuid: uuidv4(),
    isNewsletterSubEnabled: false,
    canShowExistingUserPage: false,
    checkoutToken: null,
    studentFlow: null,
    isProvidedQueryParams: true,
    canAddonSelectionSkip: false,

    productFamiliesRequestStatus: REQUEST_STATUS.IDLE,
    checkoutStateRequestStatus: REQUEST_STATUS.IDLE,
    showsGstRequestStatus: REQUEST_STATUS.IDLE
};

const userSettingsSlice = createSlice({
    name: "userSettings",
    initialState,
    reducers: {
        setProductFamilies(
            state,
            action: PayloadAction<ProductFamilyInterface[]>
        ) {
            state.productFamilies = action.payload;
        },
        setProductFamiliesResponse(
            state,
            action: PayloadAction<ProductFamilyApiInterface[]>
        ) {
            state.productFamiliesResponse = action.payload;
        },
        setUserSelectedCountry(state, action: PayloadAction<string>) {
            state.userSelectedCountry = action.payload;
        },
        setShowsGst(state, action: PayloadAction<boolean>) {
            state.showsGst = action.payload;
        },
        setUserSelectedState(state, action: PayloadAction<string>) {
            state.userSelectedState = action.payload;
        },
        setUserSelectedLang(state, action: PayloadAction<string>) {
            state.userSelectedLang = action.payload;
        },
        setCurrency(state, action: PayloadAction<"AUD" | "USD">) {
            state.currency = action.payload;
        },
        setNextAttemptPage(
            state,
            action: PayloadAction<CheckoutPagesEnum | null>
        ) {
            state.nextAttemptPage = action.payload;
        },
        setParentUserUuid(state, action: PayloadAction<string>) {
            state.parentUserUuid = action.payload;
        },
        setIsNewsletterSubEnabled(state, action: PayloadAction<boolean>) {
            state.isNewsletterSubEnabled = action.payload;
        },
        setCanShowExistingUserPage(state, action: PayloadAction<boolean>) {
            state.canShowExistingUserPage = action.payload;
        },
        setCheckoutToken(state, action: PayloadAction<string | null>) {
            state.checkoutToken = action.payload;
        },
        setStudentFlow(
            state,
            action: PayloadAction<CHECKOUT_STUDENT_FLOW | null>
        ) {
            state.studentFlow = action.payload;
        },
        setIsProvidedQueryParams(state, action: PayloadAction<boolean>) {
            state.isProvidedQueryParams = action.payload;
        },
        setCanAddonSelectionSkip(state, action: PayloadAction<boolean>) {
            state.canAddonSelectionSkip = action.payload;
        },
        resetUserSettingsState() {
            return initialState;
        }
    },
    extraReducers: builder => {
        builder
            // Fetch product families
            .addCase(fetchProductFamilies.pending, state => {
                state.productFamiliesRequestStatus = REQUEST_STATUS.PENDING;
            })
            .addCase(fetchProductFamilies.fulfilled, (state, action) => {
                state.productFamiliesRequestStatus = REQUEST_STATUS.SUCCESS;
                state.productFamilies = action.payload.productFamilies;
                state.productFamiliesResponse =
                    action.payload.productFamiliesResponse;
            })
            .addCase(fetchProductFamilies.rejected, state => {
                state.productFamiliesRequestStatus = REQUEST_STATUS.ERROR;
                state.productFamilies = null;
                state.productFamiliesResponse = null;
            })

            // Post checkout state data
            .addCase(postCheckoutState.pending, state => {
                state.checkoutStateRequestStatus = REQUEST_STATUS.PENDING;
            })
            .addCase(postCheckoutState.fulfilled, (state, action) => {
                state.checkoutStateRequestStatus = REQUEST_STATUS.SUCCESS;
                state.checkoutToken = action.payload.token;
            })
            .addCase(postCheckoutState.rejected, state => {
                state.checkoutStateRequestStatus = REQUEST_STATUS.ERROR;
            })

            // Fetch per-user GST visibility (BLD2-885). On rejection we
            // leave `showsGst` at its current value — the initial state
            // defaults to true so guests and failures both render the
            // suffix, matching legacy behaviour.
            .addCase(fetchShowsGst.pending, state => {
                state.showsGstRequestStatus = REQUEST_STATUS.PENDING;
            })
            .addCase(fetchShowsGst.fulfilled, (state, action) => {
                state.showsGstRequestStatus = REQUEST_STATUS.SUCCESS;
                state.showsGst = action.payload.showsGst;
            })
            .addCase(fetchShowsGst.rejected, state => {
                state.showsGstRequestStatus = REQUEST_STATUS.ERROR;
            });
    }
});

export const {
    setProductFamilies,
    setUserSelectedCountry,
    setShowsGst,
    setUserSelectedState,
    setUserSelectedLang,
    setCurrency,
    setNextAttemptPage,
    setParentUserUuid,
    setIsNewsletterSubEnabled,
    setCanShowExistingUserPage,
    resetUserSettingsState,
    setCheckoutToken,
    setStudentFlow,
    setIsProvidedQueryParams,
    setCanAddonSelectionSkip
} = userSettingsSlice.actions;

export default userSettingsSlice.reducer;
